/**
 * Rendered widths for the revamp's images, as `sizes` strings.
 *
 * Each is measured off the 1920 artboard the sections are laid out on (and the
 * 414 one on mobile), so the CDN serves a picture close to the box it lands in
 * rather than the full-size original. The hero entries are shared with the
 * route's `meta` preload — the preload only matches the rendered image while
 * both use the same string, so read them from here rather than restating one.
 */
import { getImagesSizes } from '~/utils/image-sizes-utils';

/** Standard hero: full width less the section's 200px side insets. */
export const REVAMP_HERO_SIZES = getImagesSizes(1520);
/** Flexi title section: the photo column beside the copy (746 of the grid). */
export const REVAMP_FLEXI_HERO_SIZES = getImagesSizes(746);
/** Premium title section: the banner runs full bleed. */
export const REVAMP_PREMIUM_HERO_SIZES = getImagesSizes(1920);

/** Landscape body image, on the same 200px insets as the hero. */
export const REVAMP_IMAGE_WIDE_SIZES = getImagesSizes(1520);
/** Portrait body image and the two-up duo module: a 900 column, full width on mobile. */
export const REVAMP_IMAGE_COLUMN_SIZES = getImagesSizes(900);
/** The small image in the image + paragraph spread. */
export const REVAMP_IMAGE_SPREAD_SIZES = getImagesSizes({
    desktop: 452,
    mobile: 378,
});
/** Three-up gallery row; two-up on mobile. */
export const REVAMP_GALLERY_TRIO_SIZES = getImagesSizes({
    desktop: 592,
    mobile: 181,
});
/** Four-up gallery, collection and Discover More rows; two-up on mobile. */
export const REVAMP_TILE_SIZES = getImagesSizes({
    desktop: 438,
    mobile: 181,
});

/** The author rail avatar renders at a fixed 64px. */
export const REVAMP_AVATAR_WIDTH = 64;

interface RevampHeroSource {
    /** The tier gates, from the revamp loader data. */
    premiumBanner: boolean;
    flexiTitle: boolean;
    /** The landscape TopBanner, behind the standard and flexi sections. */
    bannerImageUrl?: string;
    /** The premium section's own portrait banner — a different image entirely. */
    premiumBannerImageUrl?: string;
}

/**
 * Which image the article's hero actually renders, and at what width.
 *
 * This mirrors `ArticleRevampPage`'s title-section branching, gate for gate:
 * premium wins when it has its own banner, then flexi, then the standard hero,
 * and each falls back when its image is missing. The route's `meta` preloads
 * whatever this returns, so keep it in step with that component — a preload
 * pointing at a different image than the view renders downloads the wrong
 * bytes on the critical path and leaves the real hero to be fetched late.
 *
 * Returns undefined when the article has no hero to preload.
 */
export const getRevampHero = (
    source: RevampHeroSource,
): { src: string; sizes: string } | undefined => {
    if (source.premiumBanner && source.premiumBannerImageUrl) {
        return {
            src: source.premiumBannerImageUrl,
            sizes: REVAMP_PREMIUM_HERO_SIZES,
        };
    }

    if (!source.bannerImageUrl) return undefined;

    return {
        src: source.bannerImageUrl,
        sizes: source.flexiTitle ? REVAMP_FLEXI_HERO_SIZES : REVAMP_HERO_SIZES,
    };
};
